import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import { FaFileAlt, FaTrophy, FaChartBar, FaPrint, FaFilePdf, FaArrowLeft, FaSpinner } from 'react-icons/fa';

const ReportsTab = ({ user }) => {
    const [reports, setReports] = useState([]);
    const [reportType, setReportType] = useState(user.role === 'club_admin' ? 'project_summary' : 'club_performance');
    const [loading, setLoading] = useState(false);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [attendees, setAttendees] = useState([]);
    const [attendeesLoading, setAttendeesLoading] = useState(false);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminApi.getReports(reportType);
            if (data.success) {
                setReports(data.data.reports);
            }
        } catch (error) {
            console.error('Error fetching report:', error);
        } finally {
            setLoading(false);
        }
    }, [reportType]);

    const fetchProjectAttendees = async (projectId) => {
        setAttendeesLoading(true);
        try {
            const data = await adminApi.getProjectAttendance(projectId);
            if (data.success) {
                setAttendees(data.data.attendance || []);
            } else {
                setAttendees([]);
            }
        } catch (error) {
            console.error('Error fetching attendees:', error);
            setAttendees([]);
        } finally {
            setAttendeesLoading(false);
        }
    };

    // Special fetch for Member History (Projects attended by a specific member)
    const fetchMemberHistory = async (member) => {
        setAttendeesLoading(true);
        try {
            const data = await adminApi.getAllAttendance();
            if (data.success) {
                // Filter all attendance records for this specific member
                const history = data.data.attendance.filter(a => 
                    (member.admission_number && a.admission_number === member.admission_number) ||
                    (member.nic_number && a.nic_number === member.nic_number)
                );
                setAttendees(history);
            }
        } catch (error) {
            console.error('Error fetching member history:', error);
        } finally {
            setAttendeesLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleViewDoc = (row) => {
        setViewingDoc(row);
        setAttendees([]); // Reset
        if (reportType === 'member_rankings') {
            fetchMemberHistory(row);
        } else {
            fetchProjectAttendees(row.id || row.project_id);
        }
    };

    const handlePrint = (item = null) => {
        if (item) {
            setViewingDoc(item);
            if (reportType === 'member_rankings') {
                fetchMemberHistory(item);
            } else {
                fetchProjectAttendees(item.id || item.project_id);
            }
            setTimeout(() => {
                window.print();
            }, 1000); // Give more time for data to populate
        } else {
            window.print();
        }
    };

    const renderHeader = () => {
        if (viewingDoc) return null;
        const isAdmin = ['district_admin', 'super_admin'].includes(user.role);

        return (
            <div className="flex flex-wrap gap-2 md:gap-4 mb-8 no-print animate-premium">
                <button
                    onClick={() => { setReportType('club_performance'); setViewingDoc(null); }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'club_performance' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-400 hover:bg-slate-50 border border-slate-100'
                        }`}
                >
                    <FaChartBar /> <span className="whitespace-nowrap">Performances</span>
                </button>
                <button
                    onClick={() => { setReportType('project_summary'); setViewingDoc(null); }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'project_summary' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-400 hover:bg-slate-50 border border-slate-100'
                        }`}
                >
                    <FaFileAlt /> <span className="whitespace-nowrap">Projects</span>
                </button>
                <button
                    onClick={() => { setReportType('member_rankings'); setViewingDoc(null); }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'member_rankings' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-400 hover:bg-slate-50 border border-slate-100'
                        }`}
                >
                    <FaTrophy /> <span className="whitespace-nowrap">Rankings</span>
                </button>
                {isAdmin && (
                    <button
                        onClick={() => { setReportType('detailed_projects'); setViewingDoc(null); }}
                        className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'detailed_projects' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-400 hover:bg-slate-50 border border-slate-100'
                            }`}
                    >
                        <FaFileAlt /> <span className="whitespace-nowrap">Full Intel</span>
                    </button>
                )}
                <button
                    onClick={() => handlePrint()}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 transition-all ml-0 md:ml-auto w-full md:w-auto"
                >
                    <FaPrint /> Print List
                </button>
            </div>
        );
    };

    const renderTable = () => {
        if (viewingDoc) return null;
        if (loading) return (
            <div className="flex justify-center py-20 no-print animate-premium">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
            </div>
        );

        if (reports.length === 0) return (
            <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 no-print animate-premium">
                <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">No data records found for current query selection</p>
            </div>
        );

        return (
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)] no-print animate-premium">
                <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center bg-[#FDFDFF]">
                    <div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mb-1">Generated Insight</p>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter">
                            {reportType.replace('_', ' ')}
                        </h3>
                    </div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-4 py-2 rounded-xl">
                        {reports.length} Total Signals
                    </span>
                </div>
                <div className="bg-white">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto no-scrollbar">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    {reportType === 'project_summary' && (
                                        <>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Project Name</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Attendees</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Preview</th>
                                        </>
                                    )}
                                    {reportType === 'member_rankings' && (
                                        <>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Member Identity</th>
                                            {user.role === 'district_admin' && <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Affiliated Club</th>}
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">ID (Adm/NIC)</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Points / Activity</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Preview</th>
                                        </>
                                    )}
                                    {reportType === 'club_performance' && (
                                        <>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Identification / Title</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Category / Type</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Projects</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Core Delegate</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Impact Rate</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total Flow</th>
                                        </>
                                    )}
                                    {reportType === 'detailed_projects' && (
                                        <>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Project Initiative</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Affiliate</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                                            <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Force Size</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {reports.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                        {reportType === 'project_summary' && (
                                            <>
                                                <td className="px-10 py-6 font-black text-slate-900 text-sm">{row.name}</td>
                                                <td className="px-10 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-tight">{new Date(row.created_at).toLocaleDateString()}</td>
                                                <td className="px-10 py-6 text-center font-black text-indigo-600">{row.attendee_count}</td>
                                                <td className="px-10 py-6 text-right">
                                                    <button 
                                                        onClick={() => handleViewDoc(row)}
                                                        className="text-indigo-600 hover:text-indigo-800 transition-colors p-2 bg-indigo-50 rounded-xl"
                                                        title="View & Print Form"
                                                    >
                                                        <FaFilePdf size={18} />
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                        {reportType === 'member_rankings' && (
                                            <>
                                                <td className="px-10 py-6">
                                                    <div className="font-black text-slate-900 text-sm">{row.name}</div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-1">{row.email}</div>
                                                </td>
                                                {user.role === 'district_admin' && (
                                                    <td className="px-10 py-6 text-slate-500 font-bold text-xs uppercase tracking-tight">{row.club_name}</td>
                                                )}
                                                <td className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">{row.admission_number || row.nic_number || '-'}</td>
                                                <td className="px-10 py-6 text-center">
                                                    <span className="inline-block bg-indigo-50 text-indigo-600 px-5 py-2 rounded-xl font-black text-xs">
                                                        {row.participation_count} UNITS
                                                    </span>
                                                </td>
                                                <td className="px-10 py-6 text-right">
                                                    <button 
                                                        onClick={() => handleViewDoc(row)}
                                                        className="text-indigo-600 hover:text-indigo-800 transition-colors p-2 bg-indigo-50 rounded-xl"
                                                        title="View Member Report"
                                                    >
                                                        <FaFilePdf size={18} />
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                        {reportType === 'club_performance' && (
                                            <>
                                                <td className="px-10 py-6">
                                                    <div className="font-black text-slate-900 text-sm">{row.name}</div>
                                                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">
                                                        last active: {row.last_project_date ? new Date(row.last_project_date).toLocaleDateString() : 'Inactive'}
                                                    </div>
                                                </td>
                                                <td className="px-10 py-6 text-center">
                                                    <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 mb-1">{row.category || 'N/A'}</div>
                                                    <div className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">{row.type || 'N/A'}</div>
                                                </td>
                                                <td className="px-10 py-6 text-center font-black text-slate-900">{row.total_projects ?? 0}</td>
                                                <td className="px-10 py-6">
                                                    {row.top_member ? (
                                                        <span className="flex items-center gap-2 text-xs font-black text-slate-900">
                                                            <div className="w-6 h-6 rounded-lg bg-indigo-600 text-white flex items-center justify-center text-[10px]"><FaTrophy size={10} /></div>
                                                            {row.top_member}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-10 py-6 text-center font-black text-slate-400 text-xs">{row.avg_attendance}</td>
                                                <td className="px-10 py-6 text-right">
                                                    <span className="inline-block bg-slate-900 text-white px-5 py-2 rounded-xl font-black text-xs shadow-lg shadow-slate-900/10">
                                                        {row.total_attendance}
                                                    </span>
                                                </td>
                                            </>
                                        )}
                                        {reportType === 'detailed_projects' && (
                                            <>
                                                <td className="px-10 py-6 font-black text-slate-900 text-sm">{row.project_name}</td>
                                                <td className="px-10 py-6 text-slate-500 font-bold text-xs uppercase tracking-tight">{row.club_name}</td>
                                                <td className="px-10 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{new Date(row.created_at).toLocaleDateString()}</td>
                                                <td className="px-10 py-6 text-right font-black text-indigo-600 uppercase tracking-tighter">{row.attendee_count} FORCE</td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Grid View */}
                    <div className="md:hidden divide-y divide-slate-50 px-4">
                        {reports.map((row, idx) => (
                            <div key={idx} className="py-6 space-y-4">
                                {reportType === 'project_summary' && (
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-black text-slate-900 uppercase tracking-tight">{row.name}</div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">{new Date(row.created_at).toLocaleDateString()}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <div className="text-right">
                                                <div className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Attendees</div>
                                                <div className="text-xl font-black text-slate-900 tracking-tighter">{row.attendee_count}</div>
                                            </div>
                                            <button 
                                                onClick={() => handleViewDoc(row)}
                                                className="bg-indigo-600 text-white p-2 rounded-lg shadow-lg"
                                            >
                                                <FaFilePdf />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {reportType === 'member_rankings' && (
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-black text-slate-900 uppercase tracking-tight">{row.name}</div>
                                            <div className="text-[10px) font-bold text-slate-400 uppercase tracking-wider mt-1">{row.club_name}</div>
                                            <div className="text-[9px] text-indigo-500 font-black uppercase tracking-widest mt-2">ID: {row.admission_number || row.nic_number || '-'}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <div className="bg-indigo-50 px-4 py-2 rounded-2xl text-center border border-indigo-100">
                                                <div className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-0.5">UNITS</div>
                                                <div className="text-lg font-black text-indigo-600 tracking-tighter">{row.participation_count}</div>
                                            </div>
                                            <button 
                                                onClick={() => handleViewDoc(row)}
                                                className="bg-indigo-600 text-white p-2 rounded-lg"
                                            >
                                                <FaFilePdf />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {reportType === 'club_performance' && (
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-black text-slate-900 uppercase tracking-tight">{row.name}</div>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className="bg-slate-50 text-slate-900 text-[9px] px-3 py-1 rounded-full font-black uppercase border border-slate-100 tracking-widest">
                                                        {row.category}
                                                    </span>
                                                    <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{row.type}</span>
                                                </div>
                                            </div>
                                            <div className="bg-slate-900 px-4 py-2 rounded-2xl text-center shadow-lg shadow-slate-900/10">
                                                <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5">FLOW</div>
                                                <div className="text-lg font-black text-white tracking-tighter">{row.total_attendance}</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-4 py-4 border-y border-slate-50">
                                            <div className="text-center">
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Members</div>
                                                <div className="text-sm font-black text-slate-900 tracking-tighter">{row.member_count ?? 0}</div>
                                            </div>
                                            <div className="text-center border-x border-slate-50">
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Projects</div>
                                                <div className="text-sm font-black text-slate-900 tracking-tighter">{row.total_projects ?? 0}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Impact</div>
                                                <div className="text-sm font-black text-slate-900 tracking-tighter">{row.avg_attendance}</div>
                                            </div>
                                        </div>
                                        {row.top_member && (
                                            <div className="flex items-center gap-3 bg-indigo-50/50 p-3 rounded-2xl border border-indigo-50">
                                                <FaTrophy className="text-indigo-600 text-sm" />
                                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Leading Delegate: <span className="text-indigo-600">{row.top_member}</span></div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {reportType === 'detailed_projects' && (
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-black text-slate-900 uppercase tracking-tight">{row.project_name}</div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">{row.club_name}</div>
                                            <div className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mt-2">{new Date(row.created_at).toLocaleDateString()}</div>
                                        </div>
                                        <div className="bg-slate-50 px-4 py-2 rounded-2xl text-center border border-slate-100">
                                            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">FORCE</div>
                                            <div className="text-lg font-black text-slate-900 tracking-tighter">{row.attendee_count}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderAttendanceForm = () => {
        if (!viewingDoc) return null;
        const isMemberReport = reportType === 'member_rankings';
        const displayList = attendees.slice(0, 20); // Limit to 20 for one page
        const blankRowsCount = Math.max(0, 20 - displayList.length);

        return (
            <div className="animate-premium no-scrollbar">
                {/* On-screen Preview Tools */}
                <div className="no-print flex justify-between items-center mb-6 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                    <button 
                        onClick={() => setViewingDoc(null)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs text-slate-500 hover:bg-slate-50 transition-all border border-slate-100"
                    >
                        <FaArrowLeft /> Back to Insights
                    </button>
                    <div className="flex items-center gap-3">
                        {attendeesLoading && <FaSpinner className="animate-spin text-indigo-600" />}
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-4">Preview Mode</span>
                        <button 
                            onClick={() => window.print()}
                            className="flex items-center gap-2 px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 transition-all"
                        >
                            <FaPrint /> Print / Save PDF
                        </button>
                    </div>
                </div>

                {/* The Paper Sheet */}
                <div className="bg-slate-100/50 p-4 md:p-12 rounded-3xl flex justify-center no-scrollbar overflow-x-auto min-h-screen">
                    <div className="print-form bg-white text-black p-10 md:p-16 font-serif leading-tight shadow-[0_20px_50px_rgba(0,0,0,0.1)] w-full max-w-[800px] min-h-[1050px] relative">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-12">
                            <div className="flex items-start gap-1">
                                <span className="text-7xl font-black text-[#CBD5E1] tracking-tighter leading-[0.8] select-none">MY</span>
                                <div className="flex flex-col pt-1.5">
                                    <div className="bg-black text-white px-3 py-1 font-black text-[10px] tracking-[0.4em] uppercase leading-none">PORTAL</div>
                                    <div className="text-4xl font-black tracking-tighter text-slate-900 leading-[0.85] mt-1 relative">
                                        CLUB
                                        <div className="absolute inset-0 text-white stroke-black stroke-1 opacity-20 -z-10" style={{WebkitTextStroke: '1px black', color: 'transparent'}}>CLUB</div>
                                    </div>
                                </div>
                            </div>

                            <div className="text-center flex-1 px-4 font-serif">
                                <div className="space-y-0.5">
                                    <p className="text-[18px] font-bold leading-none uppercase tracking-widest">LEO</p>
                                    <p className="text-[18px] font-bold leading-none uppercase tracking-widest">DISTRICT</p>
                                    <p className="text-[18px] font-bold leading-none uppercase tracking-widest mb-4">306 D6 2025-26</p>
                                </div>
                                <div className="space-y-0.5 mt-6">
                                    <p className="text-[17px] font-black text-[#312E81] leading-none uppercase tracking-tighter">CLUB</p>
                                    <p className="text-[17px] font-black text-[#312E81] leading-none uppercase tracking-tighter">PERFORMANCE</p>
                                    <p className="text-[17px] font-black text-[#312E81] leading-none uppercase tracking-tighter mb-4">& EVALUATION</p>
                                </div>
                                <div className="w-24 mx-auto border-b border-slate-200 my-6" />
                                <div className="space-y-0.5">
                                    <p className="text-[16px] font-black uppercase tracking-[0.2em] leading-tight">
                                        {isMemberReport ? 'PARTICIPATION' : 'ATTENDANCE'}
                                    </p>
                                    <p className="text-[16px] font-black uppercase tracking-[0.2em] leading-tight">
                                        FORM
                                    </p>
                                </div>
                            </div>

                            <div className="h-28 w-28 flex items-center justify-center   border-black rounded-[20px] overflow-hidden bg-white">
                                <img 
                                    src="/district_logo.png" 
                                    alt="District Logo" 
                                    className="w-full h-full object-contain p-1"
                                />
                            </div>
                        </div>

                        {/* Details Table */}
                        <div className="mb-10">
                            <h4 className="font-black mb-4 text-[15px] uppercase tracking-wide border-l-8 border-black pl-4 py-1">
                                {isMemberReport ? 'Member Identification Insight' : 'Participants Attendance Report'}
                            </h4>
                            <table className="w-full border-collapse border-[3px] border-black text-[13px]">
                                <tbody>
                                    <tr className="bg-slate-50">
                                        <td className="border-[3px] border-black px-4 py-3 font-black w-1/4 uppercase tracking-widest bg-black text-white">
                                            {isMemberReport ? 'MEMBER DETAILS' : 'EVENT DETAILS'}
                                        </td>
                                        <td className="border-[3px] border-black px-4 py-3 font-black italic text-slate-400">
                                            {attendeesLoading ? 'Synchronizing Data...' : ''}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border-[3px] border-black px-4 py-3 font-bold bg-slate-50/50">
                                            {isMemberReport ? 'Name of Member' : 'Name of Event'}
                                        </td>
                                        <td className="border-[3px] border-black px-4 py-3 uppercase font-black text-[14px]">
                                            {viewingDoc.name || viewingDoc.project_name}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border-[3px] border-black px-4 py-3 font-bold bg-slate-50/50">
                                            {isMemberReport ? 'ID (Admission/NIC)' : 'Date'}
                                        </td>
                                        <td className="border-[3px] border-black px-4 py-3 uppercase font-black text-[14px]">
                                            {isMemberReport 
                                                ? (viewingDoc.admission_number || viewingDoc.nic_number || '-') 
                                                : new Date(viewingDoc.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                            }
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border-[3px] border-black px-4 py-3 font-bold bg-slate-50/50">
                                            {isMemberReport ? 'Club Affiliation' : 'Venue'}
                                        </td>
                                        <td className="border-[3px] border-black px-4 py-3 uppercase font-black text-[14px]">
                                            {isMemberReport
                                                ? (viewingDoc.club_name || user.club_name)
                                                : (viewingDoc.venue || '—')}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* List Table */}
                        <div className="mt-12">
                            <h4 className="font-black mb-4 text-[15px] uppercase tracking-wide border-l-8 border-black pl-4 py-1">
                                {isMemberReport ? 'PARTICIPATION HISTORY LOG' : 'ATTENDANCE DETAILS OF CLUB MEMBERS'}
                            </h4>
                            <table className="w-full border-collapse border-[3px] border-black text-[12px]">
                                <thead>
                                    <tr className="bg-slate-50 font-black">
                                        <th className="border-[3px] border-black px-3 py-3 text-center w-[10%] bg-black text-white uppercase tracking-widest">NO</th>
                                        <th className="border-[3px] border-black px-5 py-3 text-left w-[45%] uppercase tracking-widest">
                                            {isMemberReport ? 'PROJECT NAME' : 'NAME'}
                                        </th>
                                        <th className="border-[3px] border-black px-5 py-3 text-center w-[25%] uppercase tracking-widest">
                                            {isMemberReport ? 'DATE' : 'DESIGNATION'}
                                        </th>
                                        <th className="border-[3px) border-black px-5 py-3 text-center w-[20%] uppercase tracking-widest flex items-center justify-center gap-2">
                                            SIGNATURE {attendeesLoading && <FaSpinner className="animate-spin text-[8px]" />}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayList.map((item, i) => (
                                        <tr key={i} className="h-10">
                                            <td className="border-[3px] border-black px-3 py-3 text-center font-bold">{i + 1}</td>
                                            <td className="border-[3px] border-black px-5 py-3 uppercase font-bold">
                                                {isMemberReport ? (item.project_name || item.name) : item.member_name}
                                            </td>
                                            <td className="border-[3px] border-black px-5 py-3 text-center uppercase font-bold">
                                                {isMemberReport 
                                                    ? new Date(item.created_at).toLocaleDateString() 
                                                    : (item.position || item.admission_number || item.nic_number || 'Delegate')}
                                            </td>
                                            <td className="border-[3px] border-black px-5 py-3 text-center italic text-[9px] text-slate-300">
                                                {isMemberReport ? 'Verified Pulse' : 'Authorized'}
                                            </td>
                                        </tr>
                                    ))}
                                    {[...Array(blankRowsCount)].map((_, i) => (
                                        <tr key={`blank-${i}`} className="h-10">
                                            <td className="border-[3px] border-black px-3 py-3 text-center font-bold">{displayList.length + i + 1}</td>
                                            <td className="border-[3px] border-black px-5 py-3"></td>
                                            <td className="border-[3px] border-black px-5 py-3"></td>
                                            <td className="border-[3px] border-black px-5 py-3"></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-16 flex justify-between items-end text-[11px] font-black uppercase">
                            <div className="text-slate-400">
                                <p className="mb-2 italic">System Intelligence Insight Generated on:</p>
                                <p className="text-black">{new Date().toLocaleString()}</p>
                            </div>
                            <div className="text-center px-12 border-t-[3px] border-black pt-2">
                                <p>Authorized Signature</p>
                                <p className="text-[10px] text-slate-400 mt-1">Cabinet Officer / Project Chairperson</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto py-8">
            <style>
                {`
                    @media print {
                        @page { size: portrait; margin: 1cm; }
                        aside, header, .no-print, nav, footer { display: none !important; }
                        main { margin: 0 !important; padding: 0 !important; width: 100% !important; margin-left: 0 !important; }
                        body { background: white !important; margin: 0 !important; color: black !important; }
                        .print-form { 
                            display: block !important; 
                            visibility: visible !important;
                            box-shadow: none !important;
                            margin: 0 !important;
                            padding: 1cm !important;
                            width: 100% !important;
                            max-width: none !important;
                        }
                        * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
                    }
                `}
            </style>

            {renderHeader()}
            {renderTable()}
            {renderAttendanceForm()}
        </div>
    );
};

export default ReportsTab;
